<template>
  <div id="app">
    <Layout v-if="useLayoutPage && render && isLoginSuccess" />
    <router-view v-if="!useLayoutPage" />
    <IsLoading />
  </div>
</template>

<script lang="ts">
import {
  BroadcastTabChannelMasterTabClose,
  CheckBroadcastIsSupport,
  SetTabChannel,
} from "@/broadcastChannel/tabChannel";
import IsLoading from "@/components/element/IsLoading.vue";
import { SocketConnect } from "@/helper/notificationHelper";
import {
  ClearMasterTabFromCookies,
  ClearTabFromSessionStorage,
  GetTabIdFromSessionStorage,
  IsMasterTab,
  IsMasterTabAvailable,
  RemoveTabDB,
} from "@/helper/tabHelper";
import { CheckIndexedDBIsSupport } from "@/IndexedDB/tabIndexedDB";
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { LoadingStatusType } from "./enums/enums";
import Layout from "./views/Layout.vue";

const StoreLogIn = namespace("LogIn");

const StoreDepartment = namespace("Department");

const StoreRevenueDeduct = namespace("RevenueDeduct");

const StoreEmployee = namespace("Employee");

const StoreAccount = namespace("Account");

@Component({
  components: {
    Layout,
    IsLoading,
  },
})
export default class App extends Vue {
  private useLayoutPage = false;
  private render = true;

  @StoreLogIn.Getter
  private getLoginLoadingStatus!: LoadingStatusType;

  created() {
    SetTabChannel();
  }

  mounted() {
    window.addEventListener("focus", () => {
      if (this.getLoginLoadingStatus == LoadingStatusType.Success && !IsMasterTabAvailable()) {
        SocketConnect();
      }
    });
    window.addEventListener("beforeunload", async () => {
      if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
        const tabId = GetTabIdFromSessionStorage();
        const isMasterTab = IsMasterTab();
        if (isMasterTab) {
          ClearMasterTabFromCookies();
          BroadcastTabChannelMasterTabClose();
        }
        if (tabId) {
          ClearTabFromSessionStorage();
          await RemoveTabDB(tabId);
        }
      }
    });
  }

  destroyed() {
    localStorage.removeItem("lang");
  }

  @StoreAccount.Mutation
  private SetAccountListLoadingStatus!: (status: LoadingStatusType) => void;

  @StoreDepartment.Mutation
  private MutationDepartmentListLoadingStatus!: (status: LoadingStatusType) => void;

  @StoreRevenueDeduct.Mutation
  private MutationRevenueDeductLoadingStatus!: (status: LoadingStatusType) => void;

  @StoreEmployee.Mutation
  private MutationEmployeeLoadingStatus!: (status: LoadingStatusType) => void;

  get isLoginSuccess(): boolean {
    return this.getLoginLoadingStatus === LoadingStatusType.Success;
  }
  @Watch("$localStorage.lang", { immediate: true })
  localStorageLangChanged(newVal: any, oldVal: any) {
    if (localStorage.lang != null) {
      this.$i18n.locale = localStorage.lang;
    }
  }

  @Watch("$route", { immediate: true })
  routeChanged(newVal: any) {
    this.setWebTitle(this.$t(newVal.meta.title));
    if (newVal.name) {
      this.setUseLayoutPage(newVal.name);
    }
  }

  @Watch("$i18n.locale")
  localeChanged(newVal: any) {
    localStorage.setItem("lang", newVal);
    this.render = false;
    this.setWebTitle(this.$t(this.$route.meta?.title));
    this.SetAccountListLoadingStatus(LoadingStatusType.None);
    this.MutationDepartmentListLoadingStatus(LoadingStatusType.None);
    this.MutationRevenueDeductLoadingStatus(LoadingStatusType.None);
    this.MutationEmployeeLoadingStatus(LoadingStatusType.None);
    this.$nextTick(() => {
      this.render = true;
    });
  }

  public setUseLayoutPage(routerName: string) {
    switch (routerName) {
      case "login":
      case "selectList":
      case "logout":
      case "statement":
      case "withholdingTax":
      case "pageNotFound":
      case "sessionExpire":
      case "canNotAccessMerchant":
      case "pnd1K":
      case "pnd1KItem":
      case "update":
        this.useLayoutPage = false;
        break;

      default:
        this.useLayoutPage = true;
        break;
    }
  }

  public setWebTitle(title: any) {
    if (title) {
      document.title = title;
    }
  }
}
</script>

<style lang="scss">
@import url("./assets/plugin/Bootstap/css/bootstrap.min.css");
@import url("./assets/iconFont/css/PEAK-Icon-Pack.css");
@import url("./assets/css/animate.min.css");

@font-face {
  font-family: "Maibork-lig";
  src: url("./assets/font/sukhumvittadmai/sukhumvittadmai_lig.woff") format("woff");
  font-display: auto;
}

@font-face {
  font-family: "Maibork-bold";
  src: url("./assets/font/sukhumvittadmai_bold/sukhumvittadmai_sebold.woff") format("woff");
  font-display: auto;
}

@font-face {
  font-family: "micrenc";
  src: url("./assets/font/micrenc/micrenc.woff") format("woff");
  font-display: auto;
}

@font-face {
  font-family: "THSarabunNew";
  src: url("./assets/font/thSarabunNew/THSarabunNew.woff") format("woff");
  font-display: auto;
}

.master {
  @include master;
  .box {
    @include box;
  }
}

@page {
  size: A4;
  margin: 0;
}

@media print {
  #header,
  #footer,
  #breadcrumb,
  .headerSet {
    display: none;
  }
  p {
    font-size: 12px !important;
  }
  #statement #box {
    display: block !important;
    border: none !important;
    -webkit-print-color-adjust: exact;
    width: 272mm !important;
    padding-left: 10mm !important;
    margin: 0px !important;
  }
  #whtCertificatePaper,
  #pnd1KPaper {
    .box {
      width: 210mm !important;
      height: 296mm !important;
      margin: 0 !important;
      border: initial !important;
      border-radius: initial !important;
      box-shadow: initial !important;
      background: initial !important;
      p {
        font-size: 16px !important;
      }
      .fontSizeSmall {
        font-size: 12px !important;
      }
    }
  }
  .pnd1KItemPaper {
    width: 297mm !important;
    height: 209mm !important;
    margin: 0mm !important;
    padding: 10mm !important;
    border: initial !important;
    border-radius: initial !important;
    min-height: initial !important;
    box-shadow: initial !important;
    background: initial !important;
    p {
      font-size: 16px !important;
    }
  }
  #layout {
    background: white;
  }
  #layout .contant {
    margin: 0 !important;
    padding: 0 !important;
    width: 272mm !important;
  }
  body {
    min-width: unset !important;
    background-color: #ffffff !important;
    width: max-content !important;
    padding: 0 !important;
  }
}
</style>
